import {
	Briefcase02,
	Building04,
	ChevronLeft,
	Cube01,
	LayersThree01,
	Ticket01,
	XClose,
} from "@untitledui/icons";
import { type FC, type ReactElement, type ReactNode, useState } from "react";

import { IconButton } from "@/components/Atoms/IconButton/IconButton";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { BadgeWithDot } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { FeaturedIcon } from "@/components/foundations/featured-icon/featured-icon";
import { ErrorState } from "@/components/Molecules/EmptyState/ErrorState";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { cx } from "@/utils/cx";

import { MarkerPopupBody } from "./MarkerPopupBody";
import { MarkerPopupEntity } from "./marker-popup-entity";
import {
	MarkerPopupBodyState,
	type MarkerPopupPointDetails,
	resolvePointBodyState,
} from "./marker-popup-point-details";
import { MARKER_POPUP_TIP } from "./marker-popup-tip";

export { MarkerPopupEntity } from "./marker-popup-entity";
export { MARKER_POPUP_TIP } from "./marker-popup-tip";

export const MarkerPopupArrow = {
	BottomMiddle: "bottom-middle",
	BottomLeft: "bottom-left",
	BottomRight: "bottom-right",
	/** @deprecated Prefer {@link MarkerPopupArrow.TopMiddle} — same centered top caret. */
	Top: "top",
	TopMiddle: "top-middle",
	TopLeft: "top-left",
	TopRight: "top-right",
	/** Centered caret on the left edge (card opens to the right). */
	Left: "left",
	LeftTop: "left-top",
	LeftBottom: "left-bottom",
	/** Centered caret on the right edge (card opens to the left). */
	Right: "right",
	RightTop: "right-top",
	RightBottom: "right-bottom",
} as const;

export type MarkerPopupArrow =
	(typeof MarkerPopupArrow)[keyof typeof MarkerPopupArrow];

function isTopArrow(arrow: MarkerPopupArrow): boolean {
	return (
		arrow === MarkerPopupArrow.Top ||
		arrow === MarkerPopupArrow.TopMiddle ||
		arrow === MarkerPopupArrow.TopLeft ||
		arrow === MarkerPopupArrow.TopRight
	);
}

function isLeftArrow(arrow: MarkerPopupArrow): boolean {
	return (
		arrow === MarkerPopupArrow.Left ||
		arrow === MarkerPopupArrow.LeftTop ||
		arrow === MarkerPopupArrow.LeftBottom
	);
}

function isRightArrow(arrow: MarkerPopupArrow): boolean {
	return (
		arrow === MarkerPopupArrow.Right ||
		arrow === MarkerPopupArrow.RightTop ||
		arrow === MarkerPopupArrow.RightBottom
	);
}

type IconComponent = FC<{ className?: string }>;

interface EntityPreset {
	icon: IconComponent;
	badgeIcon: IconComponent;
	actionLabelKey: string;
}

const ENTITY_PRESETS: Record<MarkerPopupEntity, EntityPreset> = {
	[MarkerPopupEntity.Job]: {
		icon: Briefcase02,
		badgeIcon: Building04,
		actionLabelKey: "main.map.popup.openJob",
	},
	[MarkerPopupEntity.Asset]: {
		icon: Cube01,
		badgeIcon: Building04,
		actionLabelKey: "main.map.popup.openAsset",
	},
	[MarkerPopupEntity.Record]: {
		icon: LayersThree01,
		badgeIcon: Building04,
		actionLabelKey: "main.map.popup.openRecord",
	},
	[MarkerPopupEntity.Ticket]: {
		icon: Ticket01,
		badgeIcon: Ticket01,
		actionLabelKey: "main.map.popup.openTicket",
	},
};

export interface MarkerPopupProps {
	/** Figma Entity variant — sets default header/badge icons and CTA label. @default "job" */
	entity?: MarkerPopupEntity;
	/** Caret placement matching Figma Arrow. @default "bottom-middle" */
	arrow?: MarkerPopupArrow;
	/** Primary truncated title. */
	title: string;
	/** Secondary truncated line under the title. */
	subtitle?: string;
	/** Identifier shown after `#` (e.g. `"101"`). */
	idLabel?: string;
	/** Pill badge label in the data row. */
	badgeLabel?: string;
	/** Type label next to the map-pin icon. */
	typeLabel?: string;
	/** Optional status pill (Ticket-style); shown when set. */
	statusLabel?: string;
	/** Optional assignee name; shown when set. */
	assignee?: string;
	/** Optional due date; shown when set (with · when assignee also set). */
	dueDate?: string;
	/** CTA label. Defaults from `entity` when omitted. */
	actionLabel?: string;
	/** Header FeaturedIcon override. */
	icon?: IconComponent | ReactNode;
	/** Data-row badge leading icon override. */
	badgeIcon?: IconComponent;
	/** Shows the default spinner body while preserving popup chrome. @default false */
	isLoading?: boolean;
	/**
	 * Shows a compact ErrorState body while preserving popup chrome.
	 * Loading takes precedence when both are true.
	 * @default false
	 */
	isError?: boolean;
	/** ErrorState title. @default "Something Went Wrong" */
	errorTitle?: string;
	/** ErrorState description. */
	errorDescription?: string;
	/** Retry handler for the ErrorState Try Again action. */
	onRetry?: () => void;
	onClose?: () => void;
	onAction?: () => void;
	/** Non-vertex line/polygon click helper text. Hides Point details link. */
	nonVertexMessage?: string;
	/**
	 * When set, shows Point details > and a Device GPS / Coordinate / RTK panel.
	 * Map popups pass lat/lng via {@link createDeviceGpsPointDetails}.
	 */
	pointDetails?: MarkerPopupPointDetails;
	className?: string;
}

/**
 * CSS caret — the element origin is the tip. The triangle body extends toward
 * the card (away from the map anchor).
 */
function PopupCaret({
	direction,
	className,
}: {
	direction: "up" | "down" | "left" | "right";
	className?: string;
}): ReactElement {
	const triangle = {
		/** Tip at origin; body below (toward card when caret is above). */
		up: "border-x-8 border-b-[6px] border-x-transparent border-b-[var(--color-bg-primary)]",
		/** Tip at origin; body above (toward card when caret is below). */
		down: "border-x-8 border-t-[6px] border-x-transparent border-t-[var(--color-bg-primary)]",
		/** Tip at origin; body to the right. */
		left: "border-y-8 border-r-[6px] border-y-transparent border-r-[var(--color-bg-primary)]",
		/** Tip at origin; body to the left. */
		right:
			"border-y-8 border-l-[6px] border-y-transparent border-l-[var(--color-bg-primary)]",
	} as const;

	return (
		<div aria-hidden className={cx("size-0", triangle[direction], className)} />
	);
}

function isCornerArrow(arrow: MarkerPopupArrow): boolean {
	return (
		arrow === MarkerPopupArrow.BottomLeft ||
		arrow === MarkerPopupArrow.BottomRight ||
		arrow === MarkerPopupArrow.TopLeft ||
		arrow === MarkerPopupArrow.TopRight ||
		arrow === MarkerPopupArrow.LeftTop ||
		arrow === MarkerPopupArrow.LeftBottom ||
		arrow === MarkerPopupArrow.RightTop ||
		arrow === MarkerPopupArrow.RightBottom
	);
}

function isStartCornerArrow(arrow: MarkerPopupArrow): boolean {
	return (
		arrow === MarkerPopupArrow.BottomLeft ||
		arrow === MarkerPopupArrow.TopLeft ||
		arrow === MarkerPopupArrow.LeftTop ||
		arrow === MarkerPopupArrow.RightTop
	);
}

/**
 * Positions the caret tip on the outer edge of the card at middle / corner
 * inset. Uses a full-width (or full-height) rail so Top and Bottom share the
 * same tip X math — flex-centering a 16px slot left Top tips off-center.
 *
 * Tip sits on the outer edge (`top:0` / `bottom:0`) so tip-centric placement
 * (`-translate-y-full` / `top-0`) lands on the map origin with no extra gap.
 */
function CaretRow({ arrow }: { arrow: MarkerPopupArrow }): ReactElement {
	const isVerticalEdge = isLeftArrow(arrow) || isRightArrow(arrow);
	const isTopEdge = isTopArrow(arrow);
	const isLeftEdge = isLeftArrow(arrow);
	const tipInset = MARKER_POPUP_TIP.cornerInsetPx;
	const isMiddle = !isCornerArrow(arrow);
	const isStart = isStartCornerArrow(arrow);

	if (!isVerticalEdge) {
		const tipLeft = isMiddle
			? "50%"
			: isStart
				? tipInset
				: `calc(100% - ${tipInset}px)`;

		return (
			<div
				aria-hidden
				className={cx(
					"relative w-full shrink-0",
					// Kill hairline between card and caret base.
					isTopEdge ? "-mb-px" : "-mt-px",
				)}
				style={{ height: MARKER_POPUP_TIP.caretHeightPx }}
			>
				{/*
				  Tip X is % of this rail (= popup w-[300px]). Wrapper is a fixed
				  16px box so -translate-x-1/2 is identical for up and down carets.
				*/}
				<div
					className="absolute w-4 -translate-x-1/2"
					style={{
						left: tipLeft,
						height: MARKER_POPUP_TIP.caretHeightPx,
						...(isTopEdge ? { top: 0 } : { bottom: 0 }),
					}}
				>
					<div
						className={cx(
							"absolute left-1/2 -translate-x-1/2",
							isTopEdge ? "top-0" : "bottom-0",
						)}
					>
						<PopupCaret direction={isTopEdge ? "up" : "down"} />
					</div>
				</div>
			</div>
		);
	}

	const tipTop = isMiddle
		? "50%"
		: isStart
			? tipInset
			: `calc(100% - ${tipInset}px)`;

	return (
		<div
			aria-hidden
			className={cx(
				// Stretch to the card height (flex-row + items-stretch on root).
				// Avoid h-full % — parent height is content-sized and % can collapse.
				"relative shrink-0 self-stretch",
				isLeftEdge ? "-mr-px" : "-ml-px",
			)}
			style={{ width: MARKER_POPUP_TIP.caretHeightPx }}
		>
			{/*
			  Fixed 16px tip rail so -translate-y-1/2 matches for left/right carets
			  the same way the top/bottom rail uses a fixed 16px width.
			*/}
			<div
				className="absolute h-4 -translate-y-1/2"
				style={{
					top: tipTop,
					width: MARKER_POPUP_TIP.caretHeightPx,
					...(isLeftEdge ? { left: 0 } : { right: 0 }),
				}}
			>
				<div
					className={cx(
						"absolute top-1/2 -translate-y-1/2",
						isLeftEdge ? "left-0" : "right-0",
					)}
				>
					<PopupCaret direction={isLeftEdge ? "left" : "right"} />
				</div>
			</div>
		</div>
	);
}

/**
 * Map marker info card — Figma Map/MarkerPopup (5140:9533) + Body (73:3934).
 *
 * Presentational only. Entity presets supply default icons and CTA copy.
 * When `pointDetails` is set, Main shows Point details > and slides to the
 * Point panel; FeaturedIcon dissolves to Back. Optional Ticket fields
 * (`statusLabel`, `assignee`, `dueDate`) render when set.
 */
export function MarkerPopup({
	entity = MarkerPopupEntity.Job,
	arrow = MarkerPopupArrow.BottomMiddle,
	title,
	subtitle,
	idLabel,
	badgeLabel,
	typeLabel,
	statusLabel,
	assignee,
	dueDate,
	actionLabel,
	icon,
	badgeIcon,
	isLoading = false,
	isError = false,
	errorTitle = "Something Went Wrong",
	errorDescription = "Check your connection and try refreshing the page.",
	onRetry,
	onClose,
	onAction,
	nonVertexMessage,
	pointDetails,
	className,
}: MarkerPopupProps): ReactElement {
	const { t } = useAppTranslation();
	const preset = ENTITY_PRESETS[entity];
	const resolvedActionLabel = actionLabel ?? t(preset.actionLabelKey);
	const resolvedBadgeIcon = badgeIcon ?? preset.badgeIcon;
	const resolvedIcon = icon ?? preset.icon;
	const closeLabel = t("common.actions.close");
	const canShowPointDetails =
		pointDetails != null && nonVertexMessage == null && !isLoading && !isError;
	const [view, setView] = useState<"main" | "point">("main");
	const showPoint = canShowPointDetails && view === "point";
	const pointState = pointDetails
		? resolvePointBodyState(pointDetails)
		: MarkerPopupBodyState.PointDeviceGps;
	const mainState = canShowPointDetails
		? MarkerPopupBodyState.MainLink
		: MarkerPopupBodyState.MainNoLink;

	if (!canShowPointDetails && view !== "main") {
		setView("main");
	}

	const isHorizontal = isLeftArrow(arrow) || isRightArrow(arrow);
	const isTop = isTopArrow(arrow);

	let body: ReactElement;
	if (isLoading) {
		body = (
			<div
				data-testid="marker-popup-loading-body"
				className="flex min-h-[132px] items-center justify-center px-4 pb-4"
			>
				<LoadingIndicator type="line-simple" size="sm" supportingText />
			</div>
		);
	} else if (isError) {
		body = (
			<div
				data-testid="marker-popup-error-body"
				className="flex min-h-[132px] items-center justify-center px-4 pb-4"
			>
				<ErrorState
					title={errorTitle}
					description={errorDescription}
					showAction={onRetry != null}
					actionLabel="Try Again"
					onAction={onRetry}
					className="min-h-0 max-w-none gap-3 px-0 py-2"
				/>
			</div>
		);
	} else {
		const mainBody = (
			<MarkerPopupBody
				entity={entity}
				state={mainState}
				title={title}
				subtitle={subtitle}
				idLabel={idLabel}
				badgeLabel={badgeLabel}
				typeLabel={typeLabel}
				assignee={assignee}
				dueDate={dueDate}
				actionLabel={resolvedActionLabel}
				badgeIcon={resolvedBadgeIcon}
				onAction={onAction}
				onPointDetails={
					canShowPointDetails ? () => setView("point") : undefined
				}
				nonVertexMessage={nonVertexMessage}
				pointDetails={pointDetails}
			/>
		);

		if (!canShowPointDetails) {
			body = <div className="px-4 pb-4">{mainBody}</div>;
		} else {
			body = (
				<div
					className="relative overflow-hidden"
					data-testid="marker-popup-body-viewport"
				>
					<div
						className={cx(
							"px-4 pb-4",
							showPoint && "invisible pointer-events-none",
						)}
					>
						{mainBody}
					</div>
					<div
						aria-hidden={!showPoint}
						className={cx(
							"absolute inset-0 flex flex-col overflow-hidden px-4 pb-4 transition-transform duration-300 ease-out",
							showPoint
								? "translate-x-0"
								: "pointer-events-none translate-x-full",
						)}
					>
						<MarkerPopupBody
							className="min-h-0 flex-1"
							entity={entity}
							state={pointState}
							pointDetails={pointDetails}
						/>
					</div>
				</div>
			);
		}
	}

	const card = (
		<div
			className={cx(
				"flex min-w-0 flex-col rounded-xl bg-primary shadow-lg",
				// Side carets sit beside a fixed card; top/bottom use full root width.
				isHorizontal ? "w-[300px] shrink-0" : "w-full",
			)}
		>
			<div className="flex items-center justify-between overflow-hidden px-4 pt-3 pb-1">
				<div className="relative flex h-9 min-w-8 items-center">
					<div
						className={cx(
							"flex items-center transition-opacity duration-200",
							showPoint
								? "pointer-events-none absolute opacity-0"
								: "opacity-100",
						)}
					>
						<FeaturedIcon
							icon={resolvedIcon}
							size="sm"
							color="gray"
							theme="light"
						/>
					</div>
					{canShowPointDetails ? (
						<div
							className={cx(
								"flex items-center transition-opacity duration-200",
								showPoint
									? "opacity-100"
									: "pointer-events-none absolute opacity-0",
							)}
						>
							<Button
								color="link-color"
								size="sm"
								iconLeading={ChevronLeft}
								onClick={() => setView("main")}
							>
								{t("common.actions.back")}
							</Button>
						</div>
					) : null}
				</div>
				<div className="flex items-center gap-2">
					{statusLabel ? (
						<BadgeWithDot color="blue" size="sm" type="modern">
							{statusLabel}
						</BadgeWithDot>
					) : null}
					<IconButton
						hierarchy="link"
						size="sm"
						shape="round"
						icon={XClose}
						aria-label={closeLabel}
						tooltip={closeLabel}
						onClick={onClose}
					/>
				</div>
			</div>
			{body}
		</div>
	);

	return (
		<div
			className={cx(
				"relative flex",
				// Explicit 300px width so Top CaretRow (first child) resolves
				// w-full / left:50% against the card — not a collapsed flex width.
				// Bottom worked before because the card came first and set width.
				isHorizontal ? "flex-row items-stretch" : "w-[300px] flex-col",
				className,
			)}
			data-entity={entity}
			data-arrow={arrow}
		>
			{(isTop || isLeftArrow(arrow)) && <CaretRow arrow={arrow} />}
			{card}
			{!isTop && !isLeftArrow(arrow) && <CaretRow arrow={arrow} />}
		</div>
	);
}
